<script setup lang="ts">
/**
 * Кнопки в шапке GitLab-блока: поднять или погасить стенд, посмотреть флаги,
 * собрать релиз. Из макета 13:461.
 *
 * Кнопки не показывают состояние, а меняют его, поэтому у них своя проверка
 * прав: без `tracker:devops` их не видно совсем. Прятать, а не блокировать —
 * потому что заперта кнопка выглядит как поломка, а не как «вам нельзя».
 *
 * Стенд поднимается и гасится целиком: namespace `vp-f-dev-1421` один на все
 * сервисы задачи, и подтверждать «какую ветку» больше не нужно — ветку каждому
 * сервису выбирает сервер (DEV-1628).
 */
import { AppButton } from '@vinprop/design-system'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { useAuthStore } from '@/features/auth/store'

import { stopStand } from '../api'
import FlagsDialog from './FlagsDialog.vue'
import RaiseStandDialog from './RaiseStandDialog.vue'
import ReleaseDialog from './ReleaseDialog.vue'

import type { DevStand } from '@/features/issue/devinfo'

const props = defineProps<{
  issueId: string
  issueKey: string
  epicId?: string
  stands: DevStand[]
}>()

const emit = defineEmits<{ done: []; failed: [error: unknown] }>()

const { t } = useI18n()
const auth = useAuthStore()

const dialog = ref<'stand' | 'flags' | 'release' | null>(null)
const busy = ref(false)

/** Права на действия: смотреть блок можно всем, запускать — не всем. */
const allowed = computed(() => auth.can('tracker:devops'))

const live = computed(() => props.stands.filter((stand) => !stand.isRemoved))

/** Гасим по номеру стенда: он же ключ задачи в нижнем регистре. */
const drop = async () => {
  busy.value = true

  try {
    await stopStand(live.value[0]?.name || props.issueKey.toLowerCase())
    emit('done')
  } catch (failure) {
    emit('failed', failure)
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <div v-if="allowed" class="devact">
    <AppButton
      v-if="live.length"
      size="small"
      variant="secondary"
      :loading="busy"
      @click="drop"
    >
      {{ t('devops.stand.drop') }}
    </AppButton>
    <AppButton v-else size="small" variant="secondary" @click="dialog = 'stand'">
      {{ t('devops.stand.raise') }}
    </AppButton>
    <AppButton size="small" variant="secondary" @click="dialog = 'flags'">
      {{ t('devops.flags.button') }}
    </AppButton>
    <AppButton size="small" variant="secondary" @click="dialog = 'release'">
      {{ t('devops.release.button') }}
    </AppButton>

    <RaiseStandDialog
      v-if="dialog === 'stand'"
      :issue-id="issueId"
      :issue-key="issueKey"
      @done="((dialog = null), emit('done'))"
      @close="dialog = null"
    />

    <FlagsDialog
      v-if="dialog === 'flags'"
      @failed="emit('failed', $event)"
      @close="dialog = null"
    />

    <ReleaseDialog
      v-if="dialog === 'release'"
      :issue-id="issueId"
      :issue-key="issueKey"
      :epic-id="epicId"
      @done="((dialog = null), emit('done'))"
      @failed="emit('failed', $event)"
      @close="dialog = null"
    />
  </div>
</template>

<style scoped>
/* Кнопки макета: высота 32, радиус 8, тихая рамка — они рядом с заголовком
   блока и не должны спорить с ним за внимание. */
.devact {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
